/** @jsxImportSource theme-ui */
import {
    Field,
    Flex,
    Label,
    Box,
    Textarea,
    Checkbox,
    Text,
    Select,
    Radio,
    Spinner,
} from 'theme-ui';
import {
    Field as FormikField,
    FieldArray as FormikFieldArray,
    FormikProps,
    useFormikContext,
    FieldInputProps,
    FieldMetaProps,
    FieldHelperProps,
} from 'formik';
import { validateRequired } from '@/utils/validators';
import { TrashIcon } from '@heroicons/react/outline';
import Button from '@/components/Button';
import DropdownComboBox from './DropdownComboBox';
import { GeneralFieldProps, GrayFormikProps } from '@/types/types';
import CustomDatePicker from './CustomDatePicker';

// TODO: add field with prefix and suffix
// prefix="https://"
// suffix="bokamera.com"

export const GrayFormikField: React.FC<
    GrayFormikProps &
        Partial<FieldInputProps<any>> &
        Partial<FieldMetaProps<any>> &
        Partial<FieldHelperProps<any>> &
        GeneralFieldProps
> = (props) => {
    const { errors, values } = useFormikContext<any>();

    const type = props.type === 'string' ? 'text' : props.type;

    if (type === 'array') {
        // @ts-ignore
        return <FormikFieldArray backgroundColor="gray.1" {...props} />;
    }

    if (type === 'radio') {
        const options = props.options ?? [];

        return (
            <Box mb="2" sx={{ width: '100%' }}>
                {props.label ? <Label>{props.label}</Label> : null}
                {options.map((option, index) => (
                    <Label key={index}>
                        <FormikField
                            name={props.name}
                            value={option.value}
                            defaultChecked={
                                option.value ===
                                (props.values?.[props.name] ?? values?.[props.name as any])
                            }
                            as={Radio}
                        />
                        {option.label}
                    </Label>
                ))}
            </Box>
        );
    }

    if (props.type === 'textarea') {
        return (
            <Box mb="2" sx={{ width: '100%' }}>
                <Label>
                    {props.label}
                    {props.required ? (
                        <Text color="danger" ml="1">
                            *
                        </Text>
                    ) : null}
                </Label>

                <FormikField
                    backgroundColor="gray.1"
                    rows="5"
                    as={Textarea}
                    sx={{
                        maxWidth: '100%',
                        minWidth: '100%',
                    }}
                    {...props}
                    {...(props.required ? { validate: validateRequired } : {})}
                />
                {props.name && errors && errors[props.name] ? (
                    <Text color="danger">{errors[props.name]}</Text>
                ) : null}
            </Box>
        );
    }

    if (type === 'select') {
        const { options, ...restProps } = props as any;

        return (
            <Box mb="2" sx={{ '> div': { alignItems: 'center' }, select: { width: '100%' } }}>
                {props.label ? (
                    <Label>
                        {props.label}
                        {props.required ? (
                            <Text color="danger" ml="1">
                                *
                            </Text>
                        ) : null}
                    </Label>
                ) : null}
                <FormikField
                    disabled={props.isLoading}
                    backgroundColor="gray.1"
                    alignItems="center"
                    as={Select}
                    {...restProps}
                    {...(props.required ? { validate: validateRequired } : {})}
                    {...(props.isLoading ? { arrow: <Spinner size="16" /> } : {})}
                >
                    {props.placeholder ? (
                        <option key={'placeholder'} value="" disabled selected>
                            {props.placeholder}
                        </option>
                    ) : null}
                    {options?.map((option: any) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </FormikField>
            </Box>
        );
    }

    if (props.type === 'checkbox') {
        return (
            <Label
                mb="3"
                sx={{
                    cursor: 'pointer',
                    fontSize: 3,
                    color: 'primary',
                }}
            >
                <FormikField
                    as={Checkbox}
                    {...props}
                    {...(props.required ? { validate: validateRequired } : {})}
                />
                <Text>{props.label}</Text>
            </Label>
        );
    }

    if (props.type === 'datetime') {
        const { label, ...restProps } = props;

        return (
            <Box mb="2" sx={{ '> div': { alignItems: 'center' } }}>
                {label ? <Label>{label}</Label> : null}
                <CustomDatePicker {...restProps} />
            </Box>
        );
    }

    if (props.type === 'dropdownsearch' || props.type === 'dropdowncombobox') {
        return (
            <FormikField
                backgroundColor="gray.1"
                alignItems="center"
                as={DropdownComboBox}
                {...props}
            />
        );
    }

    return (
        <Box mb="2">
            <FormikField
                backgroundColor="gray.1"
                {...props}
                as={Field}
                label={
                    <>
                        {props.label}
                        {props.required ? (
                            <Text color="danger" ml="1">
                                *
                            </Text>
                        ) : null}
                    </>
                }
                {...(props.required ? { validate: validateRequired } : {})}
            />
            {props.name && errors && errors[props.name] ? (
                <Text color="danger">{errors[props.name]}</Text>
            ) : null}
        </Box>
    );
};

interface GrayFormikArrayFieldProps extends FormikProps<any> {
    name: string;
    label?: string;
}
export const GrayFormikArrayField: React.FC<GrayFormikArrayFieldProps> = ({
    children,
    ...props
}) => {
    return (
        <>
            {props.name ? <Label>{props.name}</Label> : null}
            <FormikFieldArray
                {...props}
                render={(arrayHelpers) => {
                    {
                        return props.values[props.name] && props.values[props.name].length > 0
                            ? props.values[props.name].map((_, index) => (
                                  <>
                                      <Flex
                                          key={index}
                                          sx={{
                                              gap: 3,
                                              alignItems: 'flex-end',
                                              flexGrow: 1,
                                          }}
                                      >
                                          <GrayFormikField
                                              as={Field}
                                              name={`${props.name}.${index}`}
                                              type="text"
                                          />

                                        <Flex
                                            sx={{
                                                alignItems: 'flex-end',
                                                pb: 2,
                                                gap: 1,
                                            }}
                                        >
                                            <Button
                                                variant="danger"
                                                type="button"
                                                onClick={() => arrayHelpers.remove(index)} // remove a friend from the list
                                            >
                                                <TrashIcon width={16} height={16} />
                                            </Button>
                                        </Flex>
                                    </Flex>
                                    {props.values.CompanyIds.length === index + 1 ? (
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            onClick={() => arrayHelpers.insert(index, '')} // insert an empty string at a position
                                        >
                                            Add company Id
                                        </Button>
                                    ) : null}
                                </>
                            ))
                            : null;
                    }
                }}
            />
        </>
    );
};
